import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, ShieldCheck, Mail, Landmark, Coins, LineChart, Receipt,
  Plus, Trash2, RefreshCw, X, Fingerprint, KeyRound, Mic, Smartphone, Lock,
  ChevronRight, Check, Download, Pencil, Inbox as InboxIcon, ScrollText,
} from "lucide-react";
import { hubApi } from "@/api/dataHub";
import { booksApi } from "@/api/books";
import { mistApi } from "@/api/mist";
import { useSession } from "@/context/SessionContext";
import { vaultSecurityApi, bioSignature } from "@/api/vaultSecurity";
import { enrollPasskey, listPasskeys, deletePasskey, passkeySupported, passkeyErrorMessage } from "@/lib/passkey";
import VaultActivityLog from "@/components/dashboard/VaultActivityLog";
import { ByokRegistry } from "@/components/dashboard/VaultStage7b";

const DOT = { green: "#22C55E", yellow: "#EAB308", gray: "#9CA3AF" };
const DOT_LABEL = { green: "Active", yellow: "Action needed", gray: "Coming soon" };

const StatusDot = ({ status }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="w-3 h-3 rounded-full border border-black/40" style={{ background: DOT[status] || DOT.gray }} />
    <span className="font-mono text-xs uppercase tracking-wide" style={{ color: DOT[status] || DOT.gray }}>{DOT_LABEL[status] || "—"}</span>
  </span>
);

const card = "w-full text-left border-2 border-white/15 rounded-2xl bg-white/[0.06] p-4 active:scale-[0.99] transition-transform";
const inputCls = "w-full bg-white/10 border-2 border-gold/50 rounded-xl px-3.5 py-3 font-mono text-sm text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-gold";
const btnGold = "flex items-center justify-center gap-2 bg-gold text-black border-2 border-charcoal font-mono text-sm font-black uppercase tracking-wide py-3 rounded-full shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50";

/* ------------------------------- BOTTOM SHEET ------------------------------ */
function Sheet({ title, subtitle, onClose, children, testid }) {
  return (
    <div className="absolute inset-0 z-[60] flex flex-col justify-end" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-[#161616] border-t-4 border-gold rounded-t-3xl max-h-[85%] flex flex-col shadow-[0_-6px_0_#000]">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-white/10">
          <button onClick={onClose} data-testid="hub-sheet-back" aria-label="Back" className="w-9 h-9 grid place-items-center rounded-full bg-white/10 border-2 border-gold shrink-0 active:scale-95"><ArrowLeft size={16} className="text-gold" /></button>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-2xl text-white leading-none">{title}</p>
            {subtitle && <p className="font-mono text-xs text-white/50 mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="hub-sheet-close" className="w-9 h-9 grid place-items-center rounded-full bg-white/10 border-2 border-white/25 shrink-0"><X size={16} className="text-white" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------- VOICE LOCK -------------------------------- */
function VoiceRecorder({ onDone }) {
  const [rec, setRec] = useState(false);
  const [busy, setBusy] = useState(false);
  const mr = useRef(null);
  const chunks = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      const m = new MediaRecorder(stream);
      m.ondataavailable = (e) => chunks.current.push(e.data);
      m.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        setBusy(true);
        try { const r = await hubApi.enrollVoice(blob); toast.success(r.message || "Voice lock saved"); onDone(); }
        catch (e) { toast.error(e?.response?.data?.detail || "We couldn't save that — try again"); }
        finally { setBusy(false); }
      };
      mr.current = m; m.start(); setRec(true);
      setTimeout(() => { if (m.state !== "inactive") { m.stop(); setRec(false); } }, 3200);
    } catch { toast.error("Please allow microphone access to use Voice Lock"); }
  };

  return (
    <div className="text-center space-y-3">
      <p className="font-mono text-sm text-white/70">Hold and say a short phrase like <span className="text-gold">"My voice is my key."</span></p>
      <button onMouseDown={start} onTouchStart={start} disabled={rec || busy} data-testid="hub-voice-record"
        className={`mx-auto w-24 h-24 rounded-full grid place-items-center border-4 ${rec ? "border-red-500 bg-red-500/20 animate-pulse" : "border-gold bg-gold/15"}`}>
        {busy ? <Loader2 size={30} className="text-gold animate-spin" /> : <Mic size={34} className={rec ? "text-red-400" : "text-gold"} />}
      </button>
      <p className="font-mono text-xs text-white/50">{rec ? "Listening… (3 seconds)" : busy ? "Saving…" : "Tap to record"}</p>
    </div>
  );
}

/* ------------------------------ SECURITY SETUP ----------------------------- */
// Real, working lock manager: each tile ENROLLS/DISABLES a method against the
// live vault-security endpoints and reflects the true persisted state, so
// choices survive logout. PIN opens a change panel; Code App opens a TOTP panel.
function SecuritySetup({ sec, onRefresh }) {
  const { user } = useSession();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [totp, setTotp] = useState(null); // { secret, uri } while enrolling
  const [totpCode, setTotpCode] = useState("");
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState("");

  const loadProfile = () => vaultSecurityApi.setupStatus().then(setProfile).catch(() => {});
  useEffect(() => { loadProfile(); }, []);

  // True enrolled state — setup-status is the single source of truth for every
  // method (biometric == a real device passkey, not a mock).
  const on = {
    device_lock: !!profile?.methods?.biometric,
    pin: !!profile?.methods?.pin,
    voice: !!profile?.methods?.voice,
    code_app: !!profile?.methods?.totp,
  };

  const refreshAll = async () => { await Promise.all([loadProfile(), Promise.resolve(onRefresh?.())]); };

  const enrollBiometric = async () => {
    setBusy("device_lock");
    try {
      if (on.device_lock) {
        const list = await listPasskeys();
        for (const c of (list?.credentials || [])) { await deletePasskey(c.credential_id); }
        toast.success("Fingerprint unlock turned off");
      } else {
        if (!passkeySupported()) { toast("This device can't do fingerprint here — use PIN or Voice.", { icon: "🔒" }); setBusy(""); return; }
        await enrollPasskey();
        toast.success("Fingerprint unlock is on 🔓");
      }
      await refreshAll();
    } catch (e) { toast(passkeyErrorMessage(e), { icon: "🔒" }); }
    finally { setBusy(""); }
  };

  const startTotp = async () => {
    if (on.code_app) {
      setBusy("code_app");
      try { await vaultSecurityApi.totpDisable(); toast.success("Authenticator removed"); await refreshAll(); }
      catch { toast.error("Couldn't remove authenticator"); }
      finally { setBusy(""); }
      return;
    }
    setBusy("code_app");
    try { const r = await vaultSecurityApi.totpInit(); setTotp(r); }
    catch { toast.error("Couldn't start authenticator setup"); }
    finally { setBusy(""); }
  };

  const confirmTotp = async () => {
    setBusy("code_app");
    try { await vaultSecurityApi.totpConfirm(totpCode.trim()); toast.success("Authenticator linked"); setTotp(null); setTotpCode(""); await refreshAll(); }
    catch { toast.error("Wrong code — try again"); }
    finally { setBusy(""); }
  };

  const handleTile = (key) => {
    if (key === "voice") { setVoiceOpen(true); return; }
    if (key === "pin") { setPinOpen(true); return; }
    if (key === "device_lock") { enrollBiometric(); return; }
    if (key === "code_app") { startTotp(); return; }
  };

  const opts = [
    { key: "device_lock", title: "Fingerprint", sub: "Face or fingerprint", onLabel: "On · tap to turn off", Icon: Fingerprint },
    { key: "pin", title: "Secret PIN", sub: "Set / change your code", onLabel: "Set · tap to change", Icon: KeyRound },
    { key: "voice", title: "Voice Lock", sub: "Record a phrase", onLabel: "Enrolled · tap to redo", Icon: Mic },
    { key: "code_app", title: "Code App", sub: "Authenticator (optional)", onLabel: "Linked · tap to remove", Icon: Smartphone },
  ];

  const activeCount = Object.values(on).filter(Boolean).length;

  return (
    <div className="border-2 border-gold/40 rounded-2xl bg-gold/[0.06] p-4" data-testid="hub-security-setup">
      <p className="font-serif text-2xl text-white leading-none">Set up your secure lock</p>
      <p className="font-mono text-xs text-white/55 mt-1 mb-3">Turn locks on or off any time — you can use more than one.</p>
      <div className="grid grid-cols-2 gap-2.5">
        {opts.map((o) => {
          const active = on[o.key];
          const loading = busy === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => handleTile(o.key)}
              disabled={loading}
              aria-pressed={active}
              data-testid={`hub-sec-${o.key}`}
              data-active={active ? "true" : "false"}
              className={`relative flex items-center gap-2.5 rounded-xl border-2 p-3 text-left shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60 ${
                active ? "border-emerald-400 bg-emerald-400/15" : "border-black bg-white/5"
              }`}
            >
              {active && (
                <span data-testid={`hub-sec-check-${o.key}`} className="absolute top-1.5 right-1.5 grid h-5 w-5 place-items-center rounded-full border-2 border-black bg-emerald-400">
                  <Check size={11} strokeWidth={3.5} className="text-black" />
                </span>
              )}
              {loading
                ? <Loader2 size={20} className="animate-spin text-gold" />
                : <o.Icon size={20} className={active ? "text-emerald-400" : "text-gold"} />}
              <div className="min-w-0 pr-4">
                <p className="font-mono text-sm font-bold text-white truncate">{o.title}</p>
                <p className={`font-mono text-xs truncate ${active ? "text-emerald-300" : "text-white/45"}`}>{active ? o.onLabel : o.sub}</p>
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="hub-sec-count">
        {activeCount} lock{activeCount === 1 ? "" : "s"} active
      </p>

      {voiceOpen && (
        <Sheet title="Voice Lock" subtitle="Record a 3-second phrase" onClose={() => setVoiceOpen(false)} testid="hub-voice-sheet">
          <VoiceRecorder onDone={() => { setVoiceOpen(false); refreshAll(); }} />
        </Sheet>
      )}

      {pinOpen && (
        <PinChangeSheet
          hasPin={on.pin}
          onClose={() => setPinOpen(false)}
          onDone={() => { setPinOpen(false); refreshAll(); }}
        />
      )}

      {totp && (
        <Sheet title="Code App" subtitle="Scan in your authenticator, then enter the 6-digit code" onClose={() => { setTotp(null); setTotpCode(""); }} testid="hub-totp-sheet">
          <div className="space-y-3">
            <div className="border-2 border-white/15 rounded-xl p-3 break-all font-mono text-[11px] text-white/70" data-testid="hub-totp-secret">
              {totp.uri || totp.secret}
            </div>
            <input
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="6-digit code"
              data-testid="hub-totp-code"
              className="w-full bg-white/5 border-2 border-white/15 rounded-xl px-3 py-3 font-mono text-lg tracking-[0.4em] text-center text-white focus:outline-none focus:border-gold"
            />
            <button onClick={confirmTotp} disabled={totpCode.length !== 6 || busy === "code_app"} className={`${btnGold} w-full`} data-testid="hub-totp-confirm">
              {busy === "code_app" ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Link authenticator
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/* --------------------------- PIN CHANGE / SET SHEET ------------------------ */
function PinChangeSheet({ hasPin, onClose, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const dig = (v) => v.replace(/\D/g, "").slice(0, 8);

  const submit = async () => {
    if (next.length < 4) { toast.error("New PIN must be at least 4 digits"); return; }
    if (next !== confirm) { toast.error("New PIN and confirmation don't match"); return; }
    setBusy(true);
    try {
      await vaultSecurityApi.pinChange(hasPin ? current : "", next);
      toast.success(hasPin ? "PIN changed" : "PIN set");
      onDone();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Couldn't update PIN — check your current code");
    } finally { setBusy(false); }
  };

  return (
    <Sheet title={hasPin ? "Change your PIN" : "Set your PIN"} subtitle="Numbers only, 4–8 digits" onClose={onClose} testid="hub-pin-sheet">
      <div className="space-y-3">
        {hasPin && (
          <input value={current} onChange={(e) => setCurrent(dig(e.target.value))} inputMode="numeric" placeholder="Current PIN"
            data-testid="hub-pin-current"
            className="w-full bg-white/5 border-2 border-white/15 rounded-xl px-3 py-3 font-mono text-lg tracking-[0.4em] text-center text-white focus:outline-none focus:border-gold" />
        )}
        <input value={next} onChange={(e) => setNext(dig(e.target.value))} inputMode="numeric" placeholder="New PIN"
          data-testid="hub-pin-new"
          className="w-full bg-white/5 border-2 border-white/15 rounded-xl px-3 py-3 font-mono text-lg tracking-[0.4em] text-center text-white focus:outline-none focus:border-gold" />
        <input value={confirm} onChange={(e) => setConfirm(dig(e.target.value))} inputMode="numeric" placeholder="Confirm new PIN"
          data-testid="hub-pin-confirm"
          className="w-full bg-white/5 border-2 border-white/15 rounded-xl px-3 py-3 font-mono text-lg tracking-[0.4em] text-center text-white focus:outline-none focus:border-gold" />
        <button onClick={submit} disabled={busy} className={`${btnGold} w-full`} data-testid="hub-pin-save">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {hasPin ? "Change PIN" : "Set PIN"}
        </button>
      </div>
    </Sheet>
  );
}


/* ---------------------------- CONNECTED INBOX ROW -------------------------- */
function InboxRow({ acc, onRenamed, onDisconnected }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(acc.label || acc.email || "");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!label.trim()) { toast.error("Give this inbox a name"); return; }
    setBusy(true);
    try { await hubApi.renameInbox(acc.id, label.trim()); setEditing(false); toast.success("Inbox renamed"); onRenamed?.(); }
    catch { toast.error("Could not rename inbox"); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { const r = await hubApi.disconnectInbox(acc.id); toast.success("Inbox disconnected"); onDisconnected?.(r); }
    catch { toast.error("Could not disconnect"); } finally { setBusy(false); }
  };
  const dot = acc.status === "yellow" ? "#EAB308" : "#22C55E";
  return (
    <div className="border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5" data-testid={`hub-inbox-${acc.id}`}>
      <div className="flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full border border-black/40 shrink-0" style={{ background: dot }} />
        {editing ? (
          <input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus
            className="flex-1 min-w-0 bg-white/10 border-2 border-gold/60 rounded-lg px-2.5 py-1.5 font-mono text-[13px] text-white outline-none"
            data-testid={`hub-inbox-rename-input-${acc.id}`} onKeyDown={(e) => e.key === "Enter" && save()} />
        ) : (
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[13px] font-bold text-white truncate">{acc.label || acc.email || "Gmail inbox"}</p>
            {acc.email && acc.label !== acc.email && <p className="font-mono text-[11px] text-white/40 truncate">{acc.email}</p>}
          </div>
        )}
        {editing ? (
          <button onClick={save} disabled={busy} data-testid={`hub-inbox-rename-save-${acc.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-emerald-400/60 bg-emerald-400/10 shrink-0">{busy ? <Loader2 size={13} className="animate-spin text-emerald-300" /> : <Check size={14} className="text-emerald-300" />}</button>
        ) : (
          <>
            <button onClick={() => setEditing(true)} data-testid={`hub-inbox-rename-${acc.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 shrink-0"><Pencil size={13} className="text-white/60" /></button>
            <button onClick={() => setConfirming(true)} data-testid={`hub-inbox-disconnect-${acc.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-rose-400/40 bg-rose-400/10 shrink-0"><Trash2 size={14} className="text-rose-300" /></button>
          </>
        )}
      </div>
      {confirming && (
        <div className="mt-2.5 border-2 border-rose-400/40 rounded-lg bg-rose-400/10 p-2.5" data-testid={`hub-inbox-confirm-${acc.id}`}>
          <p className="font-mono text-xs text-white/80">Disconnect this inbox and delete its scanning rules?</p>
          <div className="flex gap-2 mt-2">
            <button onClick={disconnect} disabled={busy} data-testid={`hub-inbox-confirm-yes-${acc.id}`} className="flex-1 font-mono text-xs font-bold uppercase py-2 rounded-lg bg-rose-500 text-white border-2 border-black">{busy ? "…" : "Disconnect"}</button>
            <button onClick={() => setConfirming(false)} data-testid={`hub-inbox-confirm-no-${acc.id}`} className="flex-1 font-mono text-xs font-bold uppercase py-2 rounded-lg bg-white/10 text-white/70 border-2 border-white/20">Keep</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- EMAIL SHEET ------------------------------ */
function EmailSheet({ onClose, onChanged }) {
  const [rules, setRules] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const connected = accounts.length > 0;
  const load = () => hubApi.emailRules().then((d) => { setRules(d.rules); setReceipts(d.receipts || []); setAccounts(d.accounts || []); setConfigured(d.configured); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!company.trim()) { toast.error("Enter a company name"); return; }
    setBusy(true);
    try { await hubApi.addEmailRule({ company, subject, note: "" }); setCompany(""); setSubject(""); await load(); onChanged?.(); toast.success("Rule saved"); }
    catch { toast.error("Could not save rule"); } finally { setBusy(false); }
  };
  const del = async (id) => { await hubApi.delEmailRule(id); await load(); onChanged?.(); };
  const connect = async () => {
    try { const r = await hubApi.gmailConnect(); if (r.url) { window.location.href = r.url; } else { toast(r.message || "Gmail setup needed", { icon: "🔧" }); } }
    catch { toast.error("Could not start Gmail connect"); }
  };
  const scan = async () => {
    setScanning(true);
    try { const r = await hubApi.scanEmail(); if (r.ok) { toast.success(`Found ${r.receipts.length} receipt(s)`); await load(); onChanged?.(); } else { toast(r.message || "Gmail link expired. Tap to reconnect safely.", { icon: "🔗" }); await load(); } }
    catch { toast.error("Scan failed — try reconnecting"); } finally { setScanning(false); }
  };
  return (
    <Sheet title="Email Scanner" subtitle="Read receipts & save the amounts for you" onClose={onClose} testid="hub-email-sheet">
      {/* Gmail connection status */}
      <div className={`border-2 rounded-2xl p-3.5 ${loading ? "border-white/15 bg-white/5" : connected ? "border-emerald-400/50 bg-emerald-400/10" : "border-gold/40 bg-gold/[0.06]"}`} data-testid="hub-gmail-status">
        <div className="flex items-center gap-2.5">
          <span className="w-3 h-3 rounded-full border border-black/40" style={{ background: loading ? "#9CA3AF" : connected ? "#22C55E" : "#EAB308" }} />
          <p className="flex-1 font-mono text-sm font-bold text-white">{loading ? "Checking…" : connected ? "System Active" : "Setup Needed"}</p>
        </div>
        <p className="font-mono text-xs text-white/55 mt-1.5">{loading ? "Checking your connected inboxes…" : connected ? "Your Gmail is linked and reading matched receipts." : configured ? "Connect your Gmail so we can read matched receipts automatically." : "Gmail reading needs a one-time Google setup by the admin."}</p>
        {!loading && (
        <div className="flex gap-2 mt-2.5">
          {connected ? (
            <button onClick={scan} disabled={scanning} className={`${btnGold} flex-1`} data-testid="hub-email-scan">{scanning ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Scan now</button>
          ) : (
            <button onClick={connect} className={`${btnGold} flex-1`} data-testid="hub-gmail-connect">{configured ? "Connect Gmail" : "How to enable"}</button>
          )}
        </div>
        )}
      </div>

      {/* Connected inboxes */}
      {!loading && connected && (
        <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3.5 space-y-2.5" data-testid="hub-connected-inboxes">
          <div className="flex items-center gap-2">
            <InboxIcon size={15} className="text-gold" />
            <p className="flex-1 font-mono text-xs uppercase tracking-wide text-white/60">Connected Inboxes · {accounts.length}</p>
          </div>
          {accounts.map((a) => (
            <InboxRow key={a.id} acc={a}
              onRenamed={async () => { await load(); onChanged?.(); }}
              onDisconnected={async () => { await load(); onChanged?.(); }} />
          ))}
          {configured && (
            <button onClick={connect} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gold/40 font-mono text-xs font-bold uppercase text-gold active:scale-[0.99]" data-testid="hub-inbox-add-another"><Plus size={14} /> Connect another inbox</button>
          )}
        </div>
      )}

      <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3.5 space-y-2.5">
        <p className="font-mono text-sm text-white/70 leading-relaxed">Look for emails from <span className="text-gold">a company</span> where the subject says <span className="text-gold">a word</span>, and save the dollar amount.</p>
        <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name — e.g. Emergent (Stripe)" className={inputCls} data-testid="hub-email-company" />
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject word — e.g. Receipt" className={inputCls} data-testid="hub-email-subject" />
        <button onClick={add} disabled={busy} className={`${btnGold} w-full`} data-testid="hub-email-add"><Plus size={16} /> Add rule</button>
      </div>

      {loading ? <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-gold mx-auto" /></div>
        : rules.length === 0 ? <p className="font-mono text-sm text-white/40 text-center py-4">No rules yet.</p>
        : rules.map((r) => (
          <RuleRow key={r.id} rule={r} onChanged={load} onDeleted={del} />
        ))}

      {receipts.length > 0 && (
        <div className="border-2 border-white/12 rounded-2xl bg-white/5 p-3 space-y-1.5" data-testid="hub-email-receipts">
          <p className="font-mono text-xs uppercase text-white/50">Saved receipts</p>
          {receipts.map((rc) => (
            <div key={rc.id} className="flex justify-between font-mono text-sm"><span className="text-white/80 truncate">{rc.merchant}</span><span className="text-emerald-300">${rc.amount_extracted}</span></div>
          ))}
        </div>
      )}
    </Sheet>
  );
}

/* ------------------------------ EMAIL RULE ROW ----------------------------- */
function RuleRow({ rule, onChanged, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [company, setCompany] = useState(rule.company || "");
  const [subject, setSubject] = useState(rule.subject || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!company.trim()) { toast.error("Enter a company name"); return; }
    setBusy(true);
    try { await hubApi.editEmailRule(rule.id, { company: company.trim(), subject: subject.trim(), note: "" }); setEditing(false); toast.success("Rule updated"); onChanged?.(); }
    catch { toast.error("Couldn't update rule"); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="border-2 border-gold/50 rounded-xl bg-white/5 p-3 space-y-2" data-testid={`hub-email-rule-edit-${rule.id}`}>
        <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" className={inputCls} data-testid={`hub-email-rule-edit-company-${rule.id}`} />
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject word (optional)" className={inputCls} data-testid={`hub-email-rule-edit-subject-${rule.id}`} />
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className={`${btnGold} flex-1`} data-testid={`hub-email-rule-save-${rule.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save</button>
          <button onClick={() => setEditing(false)} className="px-4 rounded-full border-2 border-white/20 bg-white/5 font-mono text-xs font-bold uppercase text-white/70" data-testid={`hub-email-rule-cancel-${rule.id}`}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5" data-testid={`hub-email-rule-${rule.id}`}>
      <Mail size={16} className="text-gold shrink-0" />
      <div className="flex-1 min-w-0"><p className="font-mono text-sm font-bold text-white truncate">{rule.company}</p><p className="font-mono text-xs text-white/50 truncate">subject: {rule.subject || "any"}</p></div>
      <button onClick={() => setEditing(true)} data-testid={`hub-email-rule-edit-btn-${rule.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5"><Pencil size={13} className="text-white/60" /></button>
      <button onClick={() => onDeleted?.(rule.id)} data-testid={`hub-email-del-${rule.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5"><Trash2 size={14} className="text-white/60" /></button>
    </div>
  );
}

/* ----------------------------- SAVED EMAILS SHEET -------------------------- */
function SavedEmailsSheet({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const load = () => hubApi.emailRules().then(setData).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const receipts = data?.receipts || [];
  const rules = data?.rules || [];
  const connected = (data?.accounts || []).length > 0;
  const scan = async () => {
    setScanning(true);
    try {
      const r = await hubApi.scanEmail();
      if (r.ok) { toast.success(`Saved ${r.receipts.length} email${r.receipts.length === 1 ? "" : "s"}`); await load(); }
      else { toast(r.message || "Connect your Gmail first in Email Scanner", { icon: "🔗" }); }
    } catch { toast.error("Couldn't check for new emails"); } finally { setScanning(false); }
  };
  return (
    <Sheet title="Saved Emails" subtitle="Every email we've read and saved for you" onClose={onClose} testid="hub-saved-emails-sheet">
      {loading ? <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-gold mx-auto" /></div> : (
        <>
          <div className="border-2 border-gold/50 rounded-2xl bg-gold/10 p-4 text-center" data-testid="hub-saved-emails-count">
            <p className="font-serif text-4xl text-gold leading-none">{receipts.length}</p>
            <p className="font-mono text-xs uppercase tracking-widest text-white/60 mt-1">email{receipts.length === 1 ? "" : "s"} saved</p>
          </div>

          {connected && (
            <button onClick={scan} disabled={scanning} className={`${btnGold} w-full`} data-testid="hub-saved-emails-scan">
              {scanning ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Check for new emails
            </button>
          )}

          {receipts.length === 0 ? (
            <div className="border-2 border-dashed border-white/15 rounded-2xl p-5 text-center" data-testid="hub-saved-emails-empty">
              <InboxIcon size={26} className="text-white/40 mx-auto mb-2" />
              <p className="font-mono text-sm text-white/60">Nothing saved yet. Once your inbox is connected in Email Scanner, receipts show up here automatically.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="hub-saved-emails-list">
              {receipts.map((rc, i) => (
                <div key={rc.id || i} className="border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5" data-testid={`hub-saved-email-${rc.id || i}`}>
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-gold shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm font-bold text-white truncate">{rc.merchant || "Receipt"}</p>
                      <p className="font-mono text-[11px] text-white/45 truncate">{rc.date || ""}{rc.inbox ? ` · ${rc.inbox}` : ""}</p>
                    </div>
                    {rc.amount_extracted != null && <span className="font-mono text-sm font-black text-emerald-300 shrink-0">${rc.amount_extracted}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {rules.length > 0 && (
            <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3.5 space-y-2" data-testid="hub-saved-emails-watching">
              <p className="font-mono text-xs uppercase tracking-wide text-white/60">Senders we're watching · {rules.length}</p>
              {rules.map((r) => (
                <div key={r.id} className="flex items-center gap-2 font-mono text-[13px] text-white/80">
                  <span className="w-2 h-2 rounded-full bg-gold shrink-0" />
                  <span className="truncate">{r.company}</span>
                  {r.subject && <span className="text-white/40 truncate">· "{r.subject}"</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}

/* ------------------------------ MANUAL BANK ROW ---------------------------- */
function ManualBankRow({ acc, onChanged, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(acc.name || "");
  const [bal, setBal] = useState(String(acc.balance ?? ""));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name this account"); return; }
    setBusy(true);
    try {
      const last4 = (acc.masked || "").replace(/\D/g, "").slice(-4);
      await hubApi.editManualBank(acc.id, { name: name.trim(), balance: parseFloat(bal) || 0, last4 });
      setEditing(false); toast.success("Account updated"); onChanged?.();
    } catch { toast.error("Couldn't update account"); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="border-2 border-gold/50 rounded-xl bg-white/5 p-3 space-y-2" data-testid={`hub-bank-manual-edit-${acc.id}`}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Account name" className={inputCls} data-testid={`hub-bank-manual-edit-name-${acc.id}`} />
        <input value={bal} onChange={(e) => setBal(e.target.value)} inputMode="decimal" placeholder="Balance $" className={inputCls} data-testid={`hub-bank-manual-edit-balance-${acc.id}`} />
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className={`${btnGold} flex-1`} data-testid={`hub-bank-manual-edit-save-${acc.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save</button>
          <button onClick={() => setEditing(false)} className="px-4 rounded-full border-2 border-white/20 bg-white/5 font-mono text-xs font-bold uppercase text-white/70" data-testid={`hub-bank-manual-edit-cancel-${acc.id}`}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5" data-testid={`hub-bank-manual-${acc.id}`}>
      <button onClick={() => setEditing(true)} data-testid={`hub-bank-manual-open-${acc.id}`} className="flex-1 min-w-0 text-left active:scale-[0.99]">
        <p className="font-mono text-[13px] font-bold text-white truncate">{acc.name}</p>
        <p className="font-mono text-[12px] text-white/45">{acc.masked} · ${Number(acc.balance).toLocaleString()}</p>
      </button>
      <button onClick={() => setEditing(true)} data-testid={`hub-bank-manual-edit-btn-${acc.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 shrink-0"><Pencil size={13} className="text-white/60" /></button>
      <button onClick={onDeleted} data-testid={`hub-bank-manual-del-${acc.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-rose-400/40 bg-rose-400/10 shrink-0"><Trash2 size={14} className="text-rose-300" /></button>
    </div>
  );
}

/* ------------------------------ SHARE EDIT SHEET --------------------------- */
function ShareEditModal({ share, onClose, onSaved }) {
  const [units, setUnits] = useState(String(share.units ?? ""));
  const [tag, setTag] = useState(share.tag || "personal");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const u = parseFloat(units);
    if (!u || u <= 0) { toast.error("Enter how many units you own"); return; }
    setBusy(true);
    try { await hubApi.editShare(share.id, { units: u, tag }); toast.success("Holding updated"); onSaved?.(); }
    catch { toast.error("Couldn't update holding"); } finally { setBusy(false); }
  };
  return (
    <Sheet title="Edit Holding" subtitle={share.symbol} onClose={onClose} testid="hub-share-edit-sheet">
      <input value={units} onChange={(e) => setUnits(e.target.value)} type="number" inputMode="decimal" placeholder="How many units do you own?" className={inputCls} data-testid="hub-share-edit-units" />
      <div className="grid grid-cols-2 gap-1.5">
        {["personal", "business"].map((t) => (
          <button key={t} onClick={() => setTag(t)} data-testid={`hub-share-edit-tag-${t}`} className={`font-mono text-xs font-bold uppercase py-2 rounded-lg border-2 ${tag === t ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>{t}</button>
        ))}
      </div>
      <button onClick={save} disabled={busy} className={`${btnGold} w-full`} data-testid="hub-share-edit-save">{busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save changes</button>
    </Sheet>
  );
}

/* --------------------------------- BANK SHEET ------------------------------ */
function BankSheet({ onClose, isFounder }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [mName, setMName] = useState(""); const [mBal, setMBal] = useState(""); const [mLast4, setMLast4] = useState("");
  const [savingM, setSavingM] = useState(false);
  const load = () => hubApi.bank().then(setData).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const connect = async () => {
    setConnecting(true);
    try { const r = await mistApi.bankConnect(""); if (r.consent_url) { window.open(r.consent_url, "_blank"); toast.success("Opening secure bank consent…"); } }
    catch { toast.error("Bank connect is available to the founder in this build"); } finally { setConnecting(false); }
  };
  const addManual = async () => {
    if (!mName.trim()) { toast.error("Name this account"); return; }
    setSavingM(true);
    try { await hubApi.addManualBank({ name: mName.trim(), balance: parseFloat(mBal) || 0, last4: mLast4 }); setMName(""); setMBal(""); setMLast4(""); setShowManual(false); await load(); toast.success("Account added"); }
    catch { toast.error("Could not add account"); } finally { setSavingM(false); }
  };
  const delManual = async (id) => { try { await hubApi.delManualBank(id); await load(); } catch { toast.error("Could not remove"); } };
  const manual = data?.manual_accounts || [];
  return (
    <Sheet title="Bank Accounts" subtitle="Money coming in and going out" onClose={onClose} testid="hub-bank-sheet">
      {loading ? <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-gold mx-auto" /></div>
        : (
        <>
          {!data?.connected && (
            <div className="text-center space-y-3 py-2">
              <Landmark size={30} className="text-gold mx-auto" />
              <p className="font-mono text-sm text-white/70">Connect your bank securely to see your money in and out, sorted into simple categories.</p>
              <button onClick={connect} disabled={connecting} className={`${btnGold} w-full`} data-testid="hub-bank-connect">{connecting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Connect my bank</button>
            </div>
          )}

          {/* Manual accounts — matches Crypto & Shares manual tracking */}
          <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3.5 space-y-2.5" data-testid="hub-bank-manual">
            <div className="flex items-center gap-2">
              <Landmark size={15} className="text-gold" />
              <p className="flex-1 font-mono text-xs uppercase tracking-wide text-white/60">Manual accounts · {manual.length}</p>
            </div>
            {manual.map((a) => (
              <ManualBankRow key={a.id} acc={a} onChanged={load} onDeleted={() => delManual(a.id)} />
            ))}
            {showManual ? (
              <div className="space-y-2 pt-1">
                <input value={mName} onChange={(e) => setMName(e.target.value)} placeholder="Account name — e.g. Everyday Savings" className={inputCls} data-testid="hub-bank-manual-name" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={mBal} onChange={(e) => setMBal(e.target.value)} inputMode="decimal" placeholder="Balance $" className={inputCls} data-testid="hub-bank-manual-balance" />
                  <input value={mLast4} onChange={(e) => setMLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="Last 4 digits" className={inputCls} data-testid="hub-bank-manual-last4" />
                </div>
                <button onClick={addManual} disabled={savingM} className={`${btnGold} w-full`} data-testid="hub-bank-manual-save">{savingM ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save account</button>
              </div>
            ) : (
              <button onClick={() => setShowManual(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gold/40 font-mono text-xs font-bold uppercase text-gold active:scale-[0.99]" data-testid="hub-bank-add-manual"><Plus size={14} /> Add Account Manually</button>
            )}
          </div>

          {data?.connected && (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="border-2 border-emerald-400/40 rounded-xl bg-emerald-400/10 p-3"><p className="font-mono text-xs text-white/50 uppercase">Coming in</p><p className="font-mono text-lg font-black text-emerald-400">${data.incoming.toLocaleString()}</p></div>
              <div className="border-2 border-rose-400/40 rounded-xl bg-rose-400/10 p-3"><p className="font-mono text-xs text-white/50 uppercase">Going out</p><p className="font-mono text-lg font-black text-rose-400">${data.outgoing.toLocaleString()}</p></div>
            </div>
            {Object.keys(data.by_category || {}).length > 0 && (
              <div className="border-2 border-white/12 rounded-xl bg-white/5 p-3 space-y-1.5">
                <p className="font-mono text-xs uppercase text-white/50">Where it goes</p>
                {Object.entries(data.by_category).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono text-sm"><span className="text-white/80">{k}</span><span className="text-rose-300">${v.toLocaleString()}</span></div>
                ))}
              </div>
            )}
            {(data.transactions || []).slice(0, 30).map((t, i) => (
              <div key={i} className="flex items-center gap-2 border-2 border-white/10 rounded-lg bg-white/5 px-3 py-2">
                <div className="flex-1 min-w-0"><p className="font-mono text-sm text-white truncate">{t.description}</p><p className="font-mono text-xs text-white/40">{t.category} · {t.date || ""}</p></div>
                <span className={`font-mono text-sm font-bold ${t.direction === "credit" ? "text-emerald-400" : "text-rose-400"}`}>${Math.abs(t.amount).toLocaleString()}</span>
              </div>
            ))}
          </>
          )}
        </>
        )}
    </Sheet>
  );
}

/* ------------------------------- CRYPTO SHEET ------------------------------ */
const CHAINS = [
  { key: "solana", label: "Solana", ticker: "SOL" },
  { key: "ethereum", label: "Ethereum", ticker: "ETH" },
  { key: "bitcoin", label: "Bitcoin", ticker: "BTC" },
];

function CryptoEditModal({ wallet, onClose, onSaved, onRemoved }) {
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [tag, setTag] = useState("personal");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    hubApi.revealCrypto(wallet.id).then((r) => { setName(r.name || ""); setAddress(r.address || ""); setTag(r.tag || "personal"); })
      .catch(() => toast.error("Could not open this wallet")).finally(() => setLoading(false));
  }, [wallet.id]);
  const save = async () => {
    if (!name.trim() || !address.trim()) { toast.error("Add a name and a public address"); return; }
    setBusy(true);
    try { await hubApi.editCrypto(wallet.id, { name, address, tag }); toast.success("Wallet updated"); onSaved?.(); }
    catch { toast.error("Could not update wallet"); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await hubApi.delCrypto(wallet.id); toast.success("Wallet removed"); onRemoved?.(); }
    catch { toast.error("Could not remove wallet"); } finally { setBusy(false); }
  };
  return (
    <Sheet title="Edit Wallet" subtitle={`${wallet.ticker || ""} · ${wallet.name || ""}`} onClose={onClose} testid="hub-crypto-edit-sheet">
      {loading ? <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-gold mx-auto" /></div> : (
        <>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Wallet name" className={inputCls} data-testid="hub-crypto-edit-name" />
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Public wallet address" className={inputCls} data-testid="hub-crypto-edit-address" />
          <div className="grid grid-cols-2 gap-1.5">
            {["personal", "business"].map((t) => (
              <button key={t} onClick={() => setTag(t)} data-testid={`hub-crypto-edit-tag-${t}`} className={`font-mono text-xs font-bold uppercase py-2 rounded-lg border-2 ${tag === t ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>{t}</button>
            ))}
          </div>
          <button onClick={save} disabled={busy} className={`${btnGold} w-full`} data-testid="hub-crypto-edit-save">{busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save changes</button>
          {confirming ? (
            <div className="border-2 border-rose-400/40 rounded-xl bg-rose-400/10 p-3" data-testid="hub-crypto-remove-confirm">
              <p className="font-mono text-xs text-white/80">Remove this wallet from tracking?</p>
              <div className="flex gap-2 mt-2">
                <button onClick={remove} disabled={busy} data-testid="hub-crypto-remove-yes" className="flex-1 font-mono text-xs font-bold uppercase py-2 rounded-lg bg-rose-500 text-white border-2 border-black">{busy ? "…" : "Remove"}</button>
                <button onClick={() => setConfirming(false)} data-testid="hub-crypto-remove-no" className="flex-1 font-mono text-xs font-bold uppercase py-2 rounded-lg bg-white/10 text-white/70 border-2 border-white/20">Keep</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setConfirming(true)} data-testid="hub-crypto-remove" className="w-full flex items-center justify-center gap-2 py-3 rounded-full border-2 border-rose-400/50 bg-rose-400/10 font-mono text-sm font-black uppercase text-rose-300 active:scale-[0.99]"><Trash2 size={15} /> Remove wallet</button>
          )}
        </>
      )}
    </Sheet>
  );
}

function CryptoSheet({ onClose, onChanged }) {
  const [d, setD] = useState({ wallets: [], total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(""); const [address, setAddress] = useState("");
  const [network, setNetwork] = useState("solana"); const [tag, setTag] = useState("personal");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => hubApi.crypto().then(setD).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim() || !address.trim()) { toast.error("Add a name and a public address"); return; }
    setBusy(true);
    try { await hubApi.addCrypto({ name, address, network, tag }); setName(""); setAddress(""); await load(); onChanged?.(); toast.success("Wallet added"); }
    catch { toast.error("Could not add wallet"); } finally { setBusy(false); }
  };
  const refresh = async (id) => { setRefreshing(id); try { await hubApi.refreshCrypto(id); await load(); } catch { toast.error("Could not refresh balance"); } finally { setRefreshing(null); } };
  return (
    <Sheet title="Crypto Wallets" subtitle="Track balances across chains" onClose={onClose} testid="hub-crypto-sheet">
      <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3.5 space-y-2.5">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this wallet — e.g. Main Cold Wallet" className={inputCls} data-testid="hub-crypto-name" />
        <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Paste your public wallet address" className={inputCls} data-testid="hub-crypto-address" />
        <p className="font-mono text-[11px] uppercase tracking-wide text-white/40">Which chain is this?</p>
        <div className="grid grid-cols-3 gap-1.5">
          {CHAINS.map((n) => (
            <button key={n.key} onClick={() => setNetwork(n.key)} data-testid={`hub-crypto-net-${n.key}`} className={`flex flex-col items-center gap-0.5 py-2 rounded-lg border-2 ${network === n.key ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>
              <span className="font-mono text-xs font-bold">{n.label}</span>
              <span className="font-mono text-[10px] opacity-70">{n.ticker}</span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {["personal", "business"].map((t) => (
            <button key={t} onClick={() => setTag(t)} data-testid={`hub-crypto-tag-${t}`} className={`font-mono text-xs font-bold uppercase py-2 rounded-lg border-2 ${tag === t ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>{t}</button>
          ))}
        </div>
        <button onClick={add} disabled={busy} className={`${btnGold} w-full`} data-testid="hub-crypto-add">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add wallet</button>
      </div>
      {loading ? <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-gold mx-auto" /></div>
        : d.wallets.length === 0 ? <p className="font-mono text-sm text-white/40 text-center py-4">No wallets yet.</p>
        : d.wallets.map((w) => (
          <div key={w.id} className="flex items-center gap-3 border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5" data-testid={`hub-crypto-wallet-${w.id}`}>
            <button onClick={() => setEditing(w)} data-testid={`hub-crypto-open-${w.id}`} className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.99]">
              <span className="w-2.5 h-2.5 rounded-full border border-black/40 shrink-0" style={{ background: w.status === "green" ? "#22C55E" : "#9CA3AF" }} />
              <Coins size={18} className="text-gold shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[13px] font-bold text-white truncate">{w.name} <span className="text-white/40 text-[11px]">· {w.tag}</span></p>
                <p className="font-mono text-[12px] text-white/45 truncate">{w.ticker} · {w.address_masked} · {w.status === "green" && w.balance != null ? `${w.balance} ${w.ticker}` : "Updating balance…"}</p>
              </div>
            </button>
            <button onClick={() => refresh(w.id)} data-testid={`hub-crypto-refresh-${w.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 shrink-0"><RefreshCw size={13} className={`text-white/60 ${refreshing === w.id ? "animate-spin" : ""}`} /></button>
          </div>
        ))}
      {editing && <CryptoEditModal wallet={editing} onClose={() => setEditing(null)}
        onSaved={async () => { setEditing(null); await load(); onChanged?.(); }}
        onRemoved={async () => { setEditing(null); await load(); onChanged?.(); }} />}
    </Sheet>
  );
}

/* ------------------------------- SHARES SHEET ------------------------------ */
function SharesSheet({ onClose, onChanged }) {
  const [d, setD] = useState({ shares: [], total_value: 0, priced: false });
  const [loading, setLoading] = useState(true);
  const [symbol, setSymbol] = useState(""); const [units, setUnits] = useState(""); const [tag, setTag] = useState("personal");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => hubApi.shares().then(setD).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!symbol.trim() || !units) { toast.error("Add a ticker and number of units"); return; }
    setBusy(true);
    try { await hubApi.addShare({ symbol, units: parseFloat(units), tag }); setSymbol(""); setUnits(""); await load(); onChanged?.(); toast.success("Holding added"); }
    catch { toast.error("Could not add holding"); } finally { setBusy(false); }
  };
  const del = async (id) => { await hubApi.delShare(id); await load(); onChanged?.(); };
  return (
    <Sheet title="Shares & Stocks" subtitle="Track your investments" onClose={onClose} testid="hub-shares-sheet">
      <div className="border-2 border-white/15 rounded-2xl bg-white/5 p-3.5 space-y-2.5">
        <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="Ticker — e.g. AAPL, CBA.AX" className={inputCls} data-testid="hub-shares-symbol" />
        <input value={units} onChange={(e) => setUnits(e.target.value)} type="number" inputMode="decimal" placeholder="How many units do you own?" className={inputCls} data-testid="hub-shares-units" />
        <div className="grid grid-cols-2 gap-1.5">
          {["personal", "business"].map((t) => (
            <button key={t} onClick={() => setTag(t)} data-testid={`hub-shares-tag-${t}`} className={`font-mono text-xs font-bold uppercase py-2 rounded-lg border-2 ${tag === t ? "bg-gold text-black border-gold" : "bg-white/5 text-white/60 border-white/15"}`}>{t}</button>
          ))}
        </div>
        <button onClick={add} disabled={busy} className={`${btnGold} w-full`} data-testid="hub-shares-add">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add holding</button>
      </div>
      {!d.priced && <div className="border-2 border-dashed border-white/15 rounded-xl p-3 text-center"><p className="font-mono text-xs text-white/50">Live prices switch on once a market-data key is added. Your holdings are saved either way.</p></div>}
      {loading ? <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-gold mx-auto" /></div>
        : d.shares.length === 0 ? <p className="font-mono text-sm text-white/40 text-center py-4">No holdings yet.</p>
        : d.shares.map((s) => (
          <div key={s.id} className="flex items-center gap-3 border-2 border-white/12 rounded-xl bg-white/5 px-3 py-2.5" data-testid={`hub-share-${s.id}`}>
            <button onClick={() => setEditing(s)} data-testid={`hub-share-open-${s.id}`} className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.99]">
              <LineChart size={18} className="text-gold shrink-0" />
              <div className="flex-1 min-w-0"><p className="font-mono text-sm font-bold text-white truncate">{s.symbol} <span className="text-white/40 text-xs">· {s.units} units · {s.tag}</span></p><p className="font-mono text-xs text-white/45">{s.last_price != null ? `$${s.last_price} → $${s.value}` : "price pending"}</p></div>
            </button>
            <button onClick={() => del(s.id)} data-testid={`hub-share-del-${s.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-white/15 bg-white/5 shrink-0"><Trash2 size={14} className="text-white/60" /></button>
          </div>
        ))}
      {editing && <ShareEditModal share={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); onChanged?.(); }} />}
    </Sheet>
  );
}

/* --------------------------------- TAX SHEET ------------------------------- */
function TaxSheet({ onClose }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { booksApi.summary().then(setS).finally(() => setLoading(false)); }, []);
  const exportPdf = async () => {
    setBusy(true);
    try { await booksApi.exportDocument(); toast.success("Tax document generated — find it in your books"); }
    catch { toast.error("Could not generate document"); } finally { setBusy(false); }
  };
  const sym = s?.currency_symbol || "$";
  return (
    <Sheet title="Tax & Books" subtitle="Your live tax picture" onClose={onClose} testid="hub-tax-sheet">
      {loading ? <div className="py-6 text-center"><Loader2 size={18} className="animate-spin text-gold mx-auto" /></div> : (
        <>
          <div className="border-2 border-gold/50 rounded-2xl bg-gold/10 p-4 text-center">
            <p className="font-mono text-xs uppercase tracking-widest text-white/60">Estimated tax · {s?.tax_year}</p>
            <p className="font-serif text-4xl text-gold leading-none mt-1">{sym}{(s?.liability?.income_tax || 0).toLocaleString()}</p>
            <p className="font-mono text-xs text-white/50 mt-1">Net {sym}{(s?.net || 0).toLocaleString()} · {s?.country}</p>
          </div>
          <button onClick={exportPdf} disabled={busy} className={`${btnGold} w-full`} data-testid="hub-tax-export">{busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Export PDF</button>
          <div className="border-2 border-dashed border-white/15 rounded-xl p-3 flex items-center gap-2">
            <span className="font-mono text-xs uppercase px-2 py-0.5 rounded-full bg-white/10 text-white/60 border border-white/20">Soon</span>
            <p className="font-mono text-xs text-white/50">Automatic bank linking into your books.</p>
          </div>
        </>
      )}
    </Sheet>
  );
}

/* ------------------------------ LOCKED OVERLAY ----------------------------- */
function LockedOverlay({ message, onBack }) {
  const [joined, setJoined] = useState(false);
  const join = async () => { try { await hubApi.joinWaitlist(); setJoined(true); toast.success("You're on the list!"); } catch { toast.error("Try again in a moment"); } };
  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-[#0F0F0F]" data-testid="hub-locked-overlay">
      <div className="flex items-center px-4 pt-4">
        <button onClick={onBack} data-testid="hub-locked-back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center"><ArrowLeft size={18} className="text-gold" /></button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
        <div className="w-20 h-20 rounded-3xl bg-gold/15 border-2 border-gold grid place-items-center mb-5"><Lock size={34} className="text-gold" /></div>
        <p className="font-serif text-3xl text-white leading-tight">{message}</p>
        <button onClick={join} disabled={joined} data-testid="hub-waitlist-btn" className={`${btnGold} mt-7 px-8`}>{joined ? <><Check size={16} /> You're on the list</> : "Join the compliance waitlist"}</button>
      </div>
    </div>
  );
}

/* --------------------------------- MAIN HUB -------------------------------- */
export default function PersonalDataHub({ onBack }) {
  const { user } = useSession();
  const [access, setAccess] = useState(null);
  const [sec, setSec] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null); // email | bank | crypto | shares | tax

  const loadSummary = () => hubApi.summary().then(setSummary).catch(() => {});
  const loadSec = () => hubApi.security().then(setSec).catch(() => {});

  useEffect(() => {
    (async () => {
      try {
        const a = await hubApi.access(); setAccess(a);
        if (a.unlocked) { await Promise.all([loadSummary(), loadSec()]); }
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("hub");
    if (p === "gmail_ok") { toast.success("Gmail connected — your receipts will be read automatically"); loadSummary(); }
    else if (p === "gmail_error") { toast.error("Gmail connect didn't finish — please try again"); }
    if (p) { const u = new URL(window.location.href); u.searchParams.delete("hub"); window.history.replaceState({}, "", u); }
  }, []);

  if (loading) return <div className="absolute inset-0 z-50 grid place-items-center bg-[#0F0F0F]"><Loader2 size={22} className="animate-spin text-gold" /></div>;
  if (access && !access.unlocked) return <LockedOverlay message={access.locked_message} onBack={onBack} />;

  const m = summary?.modules || {};
  const modules = [
    { key: "email", title: "Email Scanner", sub: "Read receipts & invoices for you", Icon: Mail, status: m.email?.status, count: m.email?.count },
    { key: "saved_emails", title: "Saved Emails", sub: "See every receipt we've captured", Icon: InboxIcon, status: m.email?.status },
    { key: "bank", title: "Bank Accounts", sub: "Money in and out, sorted simply", Icon: Landmark, status: m.bank?.status, count: m.bank?.count },
    { key: "crypto", title: "Crypto Wallets", sub: "Balances & net worth", Icon: Coins, status: m.crypto?.status, count: m.crypto?.count },
    { key: "shares", title: "Shares & Stocks", sub: "Your live investments", Icon: LineChart, status: m.shares?.status, count: m.shares?.count },
    { key: "tax", title: "Tax & Books", sub: "Your live tax estimate & PDF", Icon: Receipt, status: m.tax?.status },
    { key: "credentials", title: "API Keys & Credentials", sub: "Add your BYOK API keys", Icon: KeyRound, status: "green" },
  ];
  const anyLock = sec && (sec.device_lock || sec.pin || sec.voice || sec.code_app);

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-[#0F0F0F]" data-testid="personal-data-hub">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <button onClick={onBack} data-testid="hub-back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center active:scale-95"><ArrowLeft size={18} className="text-gold" /></button>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Secure</p>
          <p className="font-serif text-3xl text-white leading-none">Personal Data Hub</p>
        </div>
        <ShieldCheck size={22} className="text-gold" />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-28 space-y-3">
        {sec && <SecuritySetup sec={sec} onRefresh={loadSec} />}

        {modules.map((mod) => (
          <button key={mod.key} className={card} onClick={() => setSheet(mod.key)} data-testid={`hub-module-${mod.key}`}>
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-gold/15 border-2 border-gold grid place-items-center shrink-0"><mod.Icon size={22} className="text-gold" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-2xl text-white leading-none">{mod.title}</p>
                <p className="font-mono text-xs text-white/50 mt-1 truncate">{mod.sub}{mod.count ? ` · ${mod.count}` : ""}</p>
              </div>
              <ChevronRight size={20} className="text-white/30 shrink-0" />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <StatusDot status={mod.status || "gray"} />
            </div>
          </button>
        ))}

        {sec && anyLock && null}

        {access?.is_founder && (
          <button className={card} onClick={() => setSheet("activity")} data-testid="hub-activity-entry">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-gold/15 border-2 border-gold grid place-items-center shrink-0"><ScrollText size={22} className="text-gold" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-2xl text-white leading-none">Security Activity</p>
                <p className="font-mono text-xs text-white/50 mt-1 truncate">Recent vault access attempts</p>
              </div>
              <ChevronRight size={20} className="text-white/30 shrink-0" />
            </div>
          </button>
        )}
      </div>

      {sheet === "activity" && <VaultActivityLog onClose={() => setSheet(null)} />}

      {sheet === "email" && <EmailSheet onClose={() => setSheet(null)} onChanged={loadSummary} />}
      {sheet === "saved_emails" && <SavedEmailsSheet onClose={() => setSheet(null)} />}
      {sheet === "bank" && <BankSheet onClose={() => setSheet(null)} isFounder={access?.is_founder} />}
      {sheet === "crypto" && <CryptoSheet onClose={() => setSheet(null)} onChanged={loadSummary} />}
      {sheet === "shares" && <SharesSheet onClose={() => setSheet(null)} onChanged={loadSummary} />}
      {sheet === "tax" && <TaxSheet onClose={() => setSheet(null)} />}
      {sheet === "credentials" && (
        <Sheet title="API Keys & Credentials" subtitle="Add and manage your own service API keys (BYOK)" onClose={() => setSheet(null)} testid="hub-credentials-sheet">
          <ByokRegistry />
        </Sheet>
      )}
    </div>
  );
}
